import { createScopedLogger } from '@/utils/logging/createScopedLogger';
import { userProfileCacheService } from './userProfileCacheService';
import { supabaseRequestDeduplicator } from './supabaseRequestDeduplicator';
import { backgroundRefreshService } from './backgroundRefreshService';
import { logoutSignalService } from './logoutSignalService';
import { AppStateForegroundTracker } from '@/utils/appStateForegroundTracker';

const logger = createScopedLogger('PERIODIC-REFRESH');

const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes when app is active

class PeriodicRefreshService {
  private currentSmartuserId: string | null = null;
  private checkIntervalId: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private isRefreshing = false;
  private tracker: AppStateForegroundTracker | null = null;

  initialize(): void {
    if (this.initialized) {
      logger.info('Already initialized, skipping');
      return;
    }

    logger.info('Initializing periodic refresh service');
    this.initialized = true;

    this.tracker = new AppStateForegroundTracker({
      name: 'PERIODIC-REFRESH',
      onForeground: () => {
        logger.info('App returned to foreground, checking if refresh needed');
        this.checkAndRefreshIfNeeded();
        this.startPeriodicCheck();
      },
      onBackground: () => {
        logger.info('App going to background, pausing periodic check');
        this.stopPeriodicCheck();
      },
    });
    this.tracker.start();
  }

  setCurrentUser(smartuserId: string | null): void {
    const previousUser = this.currentSmartuserId;
    this.currentSmartuserId = smartuserId;

    logger.info(`User set: ${previousUser} -> ${smartuserId || 'none'}`);

    if (smartuserId && smartuserId !== previousUser) {
      this.checkAndRefreshIfNeeded();
      this.startPeriodicCheck();
    } else if (!smartuserId) {
      this.stopPeriodicCheck();
    }
  }

  private startPeriodicCheck(): void {
    if (this.checkIntervalId) {
      return;
    }

    logger.info(`Starting periodic check (every ${CHECK_INTERVAL_MS / 60000} minutes)`);

    this.checkIntervalId = setInterval(() => {
      if (this.tracker?.isActive() && this.currentSmartuserId) {
        this.checkAndRefreshIfNeeded();
      }
    }, CHECK_INTERVAL_MS);
  }

  private stopPeriodicCheck(): void {
    if (this.checkIntervalId) {
      logger.info('Stopping periodic check');
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  private async checkAndRefreshIfNeeded(): Promise<void> {
    if (!this.currentSmartuserId || logoutSignalService.isLoggingOut) {
      logger.info('No user logged in or logout in progress, skipping check');
      return;
    }

    if (this.isRefreshing) {
      logger.info('Refresh already in progress, skipping');
      return;
    }

    const needsRefresh = userProfileCacheService.needsPeriodicRefresh(REFRESH_INTERVAL_MS);
    const lastRefreshAge = this.getLastRefreshAge();

    logger.info(`Check: needsRefresh=${needsRefresh}, lastRefreshAge=${lastRefreshAge ? Math.round(lastRefreshAge / 60000) + 'm' : 'never'}`);

    if (needsRefresh) {
      await this.performPeriodicRefresh();
    }
  }

  private getLastRefreshAge(): number | null {
    const lastRefresh = userProfileCacheService.getLastRefreshTimestamp();
    if (lastRefresh === 0) return null;
    return Date.now() - lastRefresh;
  }

  private async performPeriodicRefresh(): Promise<void> {
    if (!this.currentSmartuserId || logoutSignalService.isLoggingOut) return;

    this.isRefreshing = true;
    const startTime = Date.now();

    try {
      logger.info(`Starting periodic refresh for user ${this.currentSmartuserId}`);

      const result = await supabaseRequestDeduplicator.fetchAllUserData(
        this.currentSmartuserId,
        { forceRefresh: true }
      );

      userProfileCacheService.setLastRefreshTimestamp();

      const duration = Date.now() - startTime;
      logger.info(`Periodic refresh completed in ${duration}ms`, {
        hasProfile: !!result.profile,
        hasGamification: !!result.gamification,
        coins: result.gamification?.totalCoins,
      });
    } catch (error) {
      logger.error('Error during periodic refresh:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  async forceRefresh(): Promise<void> {
    if (!this.currentSmartuserId) {
      logger.info('Cannot force refresh: no user logged in');
      return;
    }

    logger.info('Force refresh requested');
    await this.performPeriodicRefresh();
  }

  getStats(): {
    isInitialized: boolean;
    currentUser: string | null;
    isPeriodicCheckActive: boolean;
    lastRefreshAge: number | null;
    needsRefresh: boolean;
    refreshIntervalHours: number;
  } {
    const lastRefreshAge = this.getLastRefreshAge();
    return {
      isInitialized: this.initialized,
      currentUser: this.currentSmartuserId,
      isPeriodicCheckActive: this.checkIntervalId !== null,
      lastRefreshAge,
      needsRefresh: userProfileCacheService.needsPeriodicRefresh(REFRESH_INTERVAL_MS),
      refreshIntervalHours: REFRESH_INTERVAL_MS / (60 * 60 * 1000),
    };
  }

  cleanup(): void {
    logger.info('Cleaning up periodic refresh service');
    this.stopPeriodicCheck();
    if (this.tracker) {
      this.tracker.stop();
      this.tracker = null;
    }
    this.currentSmartuserId = null;
    this.initialized = false;
  }
}

export const periodicRefreshService = new PeriodicRefreshService();
